import React, { memo, useReducer, useEffect, useRef } from 'react';
import {
  Icon,
  Popup,
  Space,
  Text,
  Divider,
  useClickOutside,
} from '@cashfree-intl/coherent';
import PropTypes from 'prop-types';

import {
  DROPDOWN_OPTIONS_WITH_SEARCH_OPTIONS,
  TIMEZONE_INFO_CONTENT,
  TIMEZONE_MAPPED_LABELS,
  TIMEZONE_MAPPER,
} from 'containers/Header/constants';

import {
  StyledItem as DropdownItem,
  StyledMenu as DropdownMenu,
  StyledDropdownWithSearchOption,
  StyledInputField,
  TimeZone,
} from './styled';

import { dropdownReducer, initialState } from './reducer';
import {
  TOGGLE_DROPDOWN,
  SET_SEARCHED_TEXT,
  SET_DISPLAYED_OPTIONS,
  SET_SELECTED_OPTION,
} from './actions';
import CustomIcons from 'components/CustomIcons';

const TimezoneDropdown = ({
  isMerchantImportEnabled,
  selectedTimeZone,
  setSelectedTimeZone,
}) => {
  const dropdownRef = useRef(null);

  const [state, dispatch] = useReducer(dropdownReducer, {
    ...initialState,
    selectedOption: TIMEZONE_MAPPED_LABELS[selectedTimeZone],
    displayedOptions: DROPDOWN_OPTIONS_WITH_SEARCH_OPTIONS,
  });

  const { dropdownOpen, searchedText, displayedOptions } = state;

  useClickOutside(dropdownRef, () => {
    if (dropdownOpen) {
      dispatch({ type: TOGGLE_DROPDOWN });
      dispatch({ type: SET_SEARCHED_TEXT, payload: '' });
    }
  });

  useEffect(() => {
    if (searchedText === '') {
      dispatch({
        type: SET_DISPLAYED_OPTIONS,
        payload: DROPDOWN_OPTIONS_WITH_SEARCH_OPTIONS,
      });
    } else {
      const filtered = DROPDOWN_OPTIONS_WITH_SEARCH_OPTIONS.filter(option =>
        option.value.toLowerCase().includes(searchedText.toLowerCase())
      );
      dispatch({ type: SET_DISPLAYED_OPTIONS, payload: filtered });
    }
  }, [searchedText]);

  useEffect(() => {
    dispatch({
      type: SET_SELECTED_OPTION,
      payload: TIMEZONE_MAPPED_LABELS[selectedTimeZone],
    });
  }, [selectedTimeZone]);

  const handleFieldChange = e => {
    e.stopPropagation();
    dispatch({ type: SET_SEARCHED_TEXT, payload: e.target.value });
  };

  const handleDropdownClick = e => {
    e.stopPropagation();
  };

  const handleOptionSelect = option => {
    const timeZoneValue = TIMEZONE_MAPPER?.[option?.value];
    setSelectedTimeZone(timeZoneValue);
    localStorage.setItem('timeZoneValue', timeZoneValue);
    dispatch({ type: TOGGLE_DROPDOWN });
    dispatch({ type: SET_SEARCHED_TEXT, payload: '' });
  };

  const toggleDropdown = () => {
    dispatch({ type: TOGGLE_DROPDOWN });
  };

  const getOptionContent = option => (
    <Space className="pt-1 pb-1" direction="column">
      <Text color="body" variant="p14">
        {option?.value}
      </Text>
      <Text color="bodyLight" variant="p14">
        {option?.time}
      </Text>
    </Space>
  );

  if (!isMerchantImportEnabled) return null;

  return (
    <Space>
      <Popup
        content={
          <div className="content">
            {TIMEZONE_MAPPED_LABELS[selectedTimeZone] || 'No Timezone Selected'}
          </div>
        }
        basic
        trigger={
          <TimeZone onClick={toggleDropdown} style={{ cursor: 'pointer' }}>
            <CustomIcons componentName="TIMEZONE" />
          </TimeZone>
        }
      />
      {dropdownOpen && (
        <Space ref={dropdownRef}>
          <StyledDropdownWithSearchOption
            trigger={<div style={{ width: 0, height: 0 }} />}
            open={dropdownOpen}
            closeOnBlur={false}
            closeOnChange={false}
            onClick={toggleDropdown}
            style={{ zIndex: 1000 }}
          >
            <DropdownMenu onClick={handleDropdownClick}>
              <StyledInputField
                name="searchText"
                placeholder="Search timezone"
                onChange={handleFieldChange}
                className="pl-3 pr-2"
                autoFocus
              />
              <Divider />
              <div className="options-container">
                <Space direction="column">
                  {displayedOptions.map(option => (
                    <DropdownItem
                      key={option.key}
                      onClick={() => handleOptionSelect(option)}
                      selected={
                        TIMEZONE_MAPPED_LABELS[selectedTimeZone] === option.value
                      }
                    >
                      {getOptionContent(option)}
                    </DropdownItem>
                  ))}
                </Space>
              </div>
            </DropdownMenu>
          </StyledDropdownWithSearchOption>
        </Space>
      )}
    </Space>
  );
};

TimezoneDropdown.propTypes = {
  isMerchantImportEnabled: PropTypes.bool,
  selectedTimeZone: PropTypes.string.isRequired,
  setSelectedTimeZone: PropTypes.func.isRequired,
};

TimezoneDropdown.defaultProps = {
  isMerchantImportEnabled: false,
};

export default memo(TimezoneDropdown);
